import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';

// React Context for auth state/methods.
// Instead of passing auth props down 50 gazillion levels, components can just consume this.
const AuthContext = createContext();
const hostURL = config.serverUrl; 
// Small hook to use the context cleanly in components: const { ... } = useAuth()
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component that wraps the app and exposes auth state + actions.
export const AuthProvider = ({ children }) => {
  // currentUser holds the authenticated user object (or null if logged out)
  const [currentUser, setCurrentUser] = useState(null);
  // loading gates initial render until we know whether a session exists
  const [loading, setLoading] = useState(true);

  // On first mount, check if there’s an existing session on the server.
  // This typically relies on an HttpOnly cookie set during login.
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // GET /api/auth/me should return { user: {...} } if the cookie/JWT is valid
        const response = await axios.get(hostURL+'/api/auth/me', { withCredentials: true });
        setCurrentUser(response.data.user); // store user in state -> app becomes "authenticated"
      } catch (error) {
        // If not authenticated (401/403) or request fails, consider user logged out
        // 401 is expected when not logged in, so we don't log it as an error
        if (error.response?.status !== 401) {
          console.error('Auth check failed:', error);
        }
        setCurrentUser(null);
      } finally {
        // Allow children to render once we’ve resolved auth state
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Unified login/signup handler. You’re POSTing credentials to a single endpoint.
  // You could split this into login and signup if your API is separate.
  const authenticate = async (email, password) => {
    try {
      const response = await axios.post(
        hostURL+'/api/auth/authenticate',          // your API should set the cookie on success
        { email, password },
        { withCredentials: true }          // send/receive cookies
      );
      setCurrentUser(response.data.user);  // store the returned user
      return { success: true };
    } catch (error) {
      // Surface a friendly error to the UI
      return { 
        success: false, 
        error: error.response?.data?.error || 'Authentication failed' 
      };
    }
  };

  // Logs the user out server-side (clears cookie), then clears client state
  const logout = async () => {
    try {
      await axios.post(hostURL+'/api/auth/logout', {}, { withCredentials: true });
      setCurrentUser(null);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Logout failed' 
      };
    }
  };

  // Everything the app needs about auth in one object
  const value = {
    currentUser,                // { id, username, roles? } or null
    isAuthenticated: !!currentUser,
    loading,                    // useful to gate private routes during initial check
    authenticate,               // call to log in (or sign up if your API does both)
    logout                      // call to log out
  };

  return (
    // Don’t render children until we’ve finished the initial /me check
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
